if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mgActiveImage = null;
        this.mgContent = this.querySelector('.product-media-modal__content');
        this.querySelectorAll('[data-direction]').forEach((button) => {
          button.addEventListener('click', () => this.mgNavigate(Number(button.dataset.direction)));
        });
        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open') || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          if (event.target.closest('deferred-media, product-model')) return;
          event.preventDefault();
          this.mgNavigate(event.key === 'ArrowRight' ? 1 : -1);
        });
        const startDrag = (event) => {
          this.mgCancelDrag();
          if (this.mgClosing || (event.touches && event.touches.length !== 1) || this.classList.contains('mg-zoom-active')) return;
          if (event.target.closest('deferred-media, product-model')) return;
          this.mgScrollTo(this.mgActiveMedia, false);
          const left = this.mgContent.scrollLeft;
          const point = event.touches?.[0] || event;
          this.mgDrag = { x: point.clientX, y: point.clientY, dx: 0, dy: 0, left, axis: null, index: this.mgSlides().indexOf(this.mgActiveMedia) };
        };
        const moveDrag = (event) => {
          const drag = this.mgDrag;
          if (!drag) return;
          if ((event.touches && event.touches.length !== 1) || this.classList.contains('mg-zoom-active')) {
            this.mgCancelDrag();
            return;
          }
          const point = event.touches?.[0] || event;
          drag.dx = point.clientX - drag.x;
          drag.dy = point.clientY - drag.y;
          if (!drag.axis && Math.max(Math.abs(drag.dx), Math.abs(drag.dy)) > 8) {
            drag.axis = event.pointerType !== 'mouse' && Math.abs(drag.dy) > Math.abs(drag.dx) * 1.2 ? 'y' : 'x';
          }
          if (!drag.axis) return;
          event.preventDefault();
          if (drag.axis === 'x') {
            const max = this.mgContent.scrollWidth - this.mgContent.clientWidth;
            const left = drag.left - drag.dx;
            this.mgContent.scrollLeft = Math.max(0, Math.min(max, left));
            const excess = left < 0 ? -left : left > max ? max - left : 0;
            this.mgContent.style.transform = `translateX(${excess * 0.25}px)`;
          } else {
            this.mgContent.style.transform = `translateY(${drag.dy}px)`;
            this.style.backgroundColor = `rgba(255,255,255,${Math.max(0.25, 1 - Math.abs(drag.dy) / this.clientHeight)})`;
          }
        };
        const endDrag = (event) => {
          if (event.touches?.length) return;
          const drag = this.mgDrag;
          if (drag?.axis === 'y' && Math.abs(drag.dy) >= 80) {
            this.hide();
            return;
          }
          this.mgCancelDrag(true);
          if (drag?.axis === 'x') {
            const slides = this.mgSlides();
            this.mgActiveMedia = slides[Math.min(slides.length - 1, drag.index)];
            if (Math.abs(drag.dx) > Math.min(60, this.mgContent.clientWidth * 0.12)) this.mgNavigate(drag.dx < 0 ? 1 : -1);
            else this.mgScrollTo(this.mgActiveMedia, true);
          }
        };
        this.mgContent.addEventListener('touchstart', startDrag, { passive: true });
        this.mgContent.addEventListener('touchmove', moveDrag, { passive: false });
        this.mgContent.addEventListener('touchend', endDrag);
        this.mgContent.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          startDrag(event);
          if (this.mgDrag) event.target.setPointerCapture(event.pointerId);
        });
        this.mgContent.addEventListener('pointermove', (event) => {
          if (event.pointerType === 'mouse') moveDrag(event);
        });
        this.mgContent.addEventListener('pointerup', (event) => {
          if (event.pointerType === 'mouse') endDrag(event);
        });
        this.mgContent.addEventListener('dragstart', (event) => event.preventDefault());
        this.mgContent.addEventListener('lostpointercapture', () => {
          if (this.mgDrag) endDrag({});
        });
        this.mgContent.addEventListener('wheel', (event) => {
          const delta = event.shiftKey ? event.deltaY || event.deltaX : event.deltaX;
          if (!delta || (!event.shiftKey && Math.abs(delta) <= Math.abs(event.deltaY)) || this.classList.contains('mg-zoom-active')) return;
          event.preventDefault();
          const now = performance.now();
          const continuing = now < (this.mgWheelUntil || 0);
          this.mgWheelUntil = now + 180;
          if (!continuing && !this.mgDrag) this.mgNavigate(delta < 0 ? -1 : 1);
        }, { passive: false });
        this.mgContent.addEventListener('touchcancel', () => {
          this.mgCancelDrag(true);
          this.mgScrollTo(this.mgActiveMedia, false);
        }, { passive: true });
        this.mgResize = () => {
          if (!this.hasAttribute('open')) return;
          this.mgCancelDrag();
          this.dispatchEvent(new CustomEvent('mg:media-change'));
          if (this.mgActiveMedia?.tagName === 'IMG') this.mgSetImageSize(this.mgActiveMedia);
          this.mgScrollTo(this.mgActiveMedia, false);
        };
      }

      hide() {
        if (!this.hasAttribute('open') || this.mgClosing) return;
        this.mgClosing = true;
        window.removeEventListener('resize', this.mgResize);
        const image = this.mgActiveMedia;
        const target = this.mgGalleryImage(image?.dataset.mediaId) || this.mgGalleryImage(this.openedBy?.dataset.mediaId);
        this.classList.add('mg-is-closing');
        const duration = 240;
        if (!this.isConnected || !this.animate) {
          this.mgFinishClose();
          return;
        }
        const options = { duration, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' };
        const box = target?.getBoundingClientRect();
        const canReturn = image?.tagName === 'IMG' && image.complete && image.naturalWidth && box?.width > 0 && box.height > 0 && box.bottom > 0 && box.top < window.innerHeight && box.right > 0 && box.left < window.innerWidth;
        let animation;
        if (canReturn) {
          const from = image.getBoundingClientRect();
          const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
          const width = Math.min(from.width, from.height * ratio);
          const height = width / ratio;
          const x = from.left + (from.width - width) / 2;
          const y = from.top + (from.height - height) / 2;
          const ghost = this.mgReturnImage = document.createElement('img');
          ghost.className = 'product-media-modal__return-image';
          ghost.alt = '';
          ghost.src = image.currentSrc || image.src;
          ghost.style.cssText = `left:${x}px;top:${y}px;width:${width}px;height:${height}px`;
          this.appendChild(ghost);
          this.mgContent.style.opacity = '0';
          animation = ghost.animate([{ transform: 'none' }, { transform: `translate(${box.left - x}px,${box.top - y}px) scale(${box.width / width},${box.height / height})` }], options);
          this.mgBackdropAnimation = this.animate([{ backgroundColor: getComputedStyle(this).backgroundColor }, { backgroundColor: 'rgba(255,255,255,0)' }], options);
        } else {
          animation = this.animate([{ opacity: 1 }, { opacity: 0 }], options);
        }
        this.mgCloseAnimation = animation;
        animation.finished.catch(() => {}).then(() => { if (this.mgClosing) this.mgFinishClose(); });
      }

      mgFinishClose() {
        this.mgCloseAnimation?.cancel();
        this.mgBackdropAnimation?.cancel();
        this.mgCloseAnimation = this.mgBackdropAnimation = null;
        this.mgReturnImage?.remove();
        this.mgReturnImage = null;
        this.mgCancelDrag();
        this.mgContent.style.removeProperty('opacity');
        this.dispatchEvent(new CustomEvent('mg:media-change'));
        const lock = this.mgScrollLock;
        this.mgUnlockScroll();
        super.hide();
        if (lock) window.scrollTo({ left: lock.x, top: lock.y, behavior: 'instant' });
        this.classList.remove('mg-is-closing');
        this.mgClosing = false;
      }

      disconnectedCallback() {
        if (this.hasAttribute('open')) this.mgFinishClose();
        window.removeEventListener('resize', this.mgResize);
      }

      show(opener) {
        if (this.hasAttribute('open')) return;
        const body = document.body;
        this.mgScrollLock = {
          x: window.scrollX, y: window.scrollY,
          styles: ['position', 'top', 'left', 'width', 'padding-right'].map((property) =>
            [property, body.style.getPropertyValue(property), body.style.getPropertyPriority(property)])
        };
        const gap = window.innerWidth - document.documentElement.clientWidth;
        const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
        body.style.setProperty('position', 'fixed');
        body.style.setProperty('top', `${-this.mgScrollLock.y}px`);
        body.style.setProperty('left', `${-this.mgScrollLock.x}px`);
        body.style.setProperty('width', '100%');
        if (gap) body.style.setProperty('padding-right', `${padding + gap}px`);
        super.show(opener);
        this.showActiveMedia();
        window.addEventListener('resize', this.mgResize);
      }

      mgUnlockScroll() {
        this.mgScrollLock?.styles.forEach(([property, value, priority]) => {
          if (value) document.body.style.setProperty(property, value, priority);
          else document.body.style.removeProperty(property);
        });
        this.mgScrollLock = null;
      }

      mgCancelDrag(animate = false) {
        this.mgSettleAnimation?.cancel();
        const transform = this.mgContent.style.transform;
        this.mgDrag = null;
        this.mgContent.style.removeProperty('transform');
        this.style.removeProperty('background-color');
        if (animate && transform && this.mgContent.animate) {
          this.mgSettleAnimation = this.mgContent.animate([{ transform }, { transform: 'none' }], { duration: 180, easing: 'ease-out' });
        }
      }

      mgSlides() {
        return Array.from(this.mgContent.children).filter((slide) =>
          slide.hasAttribute('data-media-id') && getComputedStyle(slide).display !== 'none');
      }

      mgScrollTo(slide, smooth) {
        if (!slide) return;
        const container = this.mgContent;
        const left = this.mgSlides().indexOf(slide) * container.clientWidth;
        container.scrollTo({ left, top: 0, behavior: smooth ? 'smooth' : 'instant' });
      }

      mgNavigate(direction) {
        if (this.mgClosing) return;
        const slides = this.mgSlides();
        if (slides.length < 2) return;
        const index = Math.max(0, slides.indexOf(this.mgActiveMedia));
        window.pauseAllMedia();
        const slide = slides[(index + direction + slides.length) % slides.length];
        this.mgActivateMedia(slide);
        this.mgScrollTo(slide, true);
      }

      mgActivateMedia(slide) {
        if (this.mgActiveMedia !== slide) this.dispatchEvent(new CustomEvent('mg:media-change'));
        this.mgActiveMedia = slide;
        Array.from(this.mgContent.children).forEach((item) => {
          item.classList.toggle('active', item === slide);
          item.setAttribute('aria-hidden', String(item !== slide));
          item.inert = item !== slide;
        });
        if (slide.tagName === 'IMG') this.mgActivateImage(slide);
        else this.mgActiveImage = null;
        const single = this.mgSlides().length < 2;
        this.querySelectorAll('[data-direction]').forEach((button) => { button.hidden = single; });
      }

      mgContainer() {
        return this.querySelector('.product-media-modal__content');
      }

      mgSetImageSize(image) {
        if (image.classList.contains('mg-is-zoomed')) return;
        const container = this.mgContainer();
        const width = Number(image.getAttribute('width'));
        const height = Number(image.getAttribute('height'));
        if (!container || !width || !height) return;
        const boxWidth = Math.max(1, container.clientWidth - 2);
        const boxHeight = Math.max(1, container.clientHeight - 2);
        const fittedWidth = Math.ceil(Math.min(boxWidth, boxHeight * width / height));
        image.setAttribute('sizes', fittedWidth + 'px');
      }

      mgGalleryImage(mediaId) {
        const gallery = this.openedBy?.closest('media-gallery');
        const button = gallery && Array.from(gallery.querySelectorAll('button[data-media-id]'))
          .find((item) => item.dataset.mediaId === mediaId);
        return (button || (this.openedBy?.dataset.mediaId === mediaId ? this.openedBy : null))
          ?.closest('.product-media-container')?.querySelector('.product__media img');
      }

      mgSetPlaceholder(image) {
        const thumbnail = this.mgGalleryImage(image.dataset.mediaId);
        const frame = thumbnail?.closest('.product__media');
        let source = '';
        if (thumbnail?.complete && thumbnail.naturalWidth > 0) {
          source = `url(${JSON.stringify(thumbnail.currentSrc || thumbnail.src)})`;
        } else if (image.complete && image.naturalWidth > 0) {
          source = `url(${JSON.stringify(image.currentSrc || image.src)})`;
        } else if (frame) {
          source = getComputedStyle(frame).backgroundImage;
        }
        if (!source || source === 'none') {
          const available = thumbnail?.currentSrc || image.currentSrc || image.src;
          if (!available) return;
          source = `url(${JSON.stringify(available)})`;
        }
        image.style.setProperty('background-image', source, 'important');
        image.style.setProperty('background-position', 'center', 'important');
        image.style.setProperty('background-repeat', 'no-repeat', 'important');
        image.style.setProperty('background-size', 'contain', 'important');
      }

      async mgImageReady(image) {
        if (!image.complete) {
          await new Promise((resolve, reject) => {
            const cleanup = () => {
              image.removeEventListener('load', loaded);
              image.removeEventListener('error', failed);
            };
            const loaded = () => { cleanup(); resolve(); };
            const failed = () => { cleanup(); reject(new Error('Product image did not load')); };
            image.addEventListener('load', loaded, { once: true });
            image.addEventListener('error', failed, { once: true });
          });
        }
        if (!image.naturalWidth) throw new Error('Product image has no decoded pixels');
        if (typeof image.decode === 'function') await image.decode();
      }

      mgActivateImage(image) {
        if (!this.hasAttribute('open')) return;
        this.mgActiveImage = image;
        const load = image.mgLoad = {};
        this.mgSetPlaceholder(image);
        image.setAttribute('decoding', 'async');
        image.setAttribute('fetchpriority', 'high');
        image.setAttribute('loading', 'eager');
        this.mgWarmNext(image);
        let ready;
        if (image.dataset.fullSrc && (image.hasAttribute('srcset') || image.getAttribute('src') !== image.dataset.fullSrc)) {
          const original = new Image();
          original.decoding = 'async';
          original.fetchPriority = 'high';
          original.src = image.dataset.fullSrc;
          ready = this.mgImageReady(original).then(() => {
            if (image.mgLoad !== load || !this.hasAttribute('open') || this.mgActiveImage !== image || this.mgClosing) return;
            image.setAttribute('src', image.dataset.fullSrc);
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            return this.mgImageReady(image);
          }).catch(() => this.mgImageReady(image));
        } else {
          ready = this.mgImageReady(image);
        }
        ready.then(() => {
          if (image.mgLoad !== load || !image.complete || !image.naturalWidth) return;
          ['background-image', 'background-position', 'background-repeat', 'background-size']
            .forEach((property) => image.style.removeProperty(property));
        }).catch(() => {});
      }

      mgWarmNext(image) {
        if (!this.hasAttribute('open') || document.hidden) return;
        if (navigator.connection?.saveData) return;
        const next = image.nextElementSibling;
        if (!next || next.tagName !== 'IMG') return;
        this.mgSetPlaceholder(next);
        this.mgSetImageSize(next);
        next.setAttribute('decoding', 'async');
        next.setAttribute('fetchpriority', 'low');
        next.setAttribute('loading', 'eager');
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        if (!mediaId) return;
        const activeMedia = Array.from(this.querySelectorAll('[data-media-id]'))
          .find((element) => element.getAttribute('data-media-id') === mediaId);
        if (!activeMedia) return;
        this.mgActivateMedia(activeMedia);
        requestAnimationFrame(() => {
          if (!this.hasAttribute('open') || this.mgActiveMedia !== activeMedia) return;
          this.mgScrollTo(activeMedia, false);
        });
        const template = activeMedia.querySelector('template');
        if (activeMedia.nodeName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
      }
    }
  );
}

